import React from 'react';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';

const tabNavVariants = cva(
  'relative inline-flex items-center gap-1 p-1 rounded-xl',
  {
    variants: {
      variant: {
        underline: 'border-b border-border rounded-none bg-transparent',
        pill: 'bg-muted',
        glass: 'backdrop-blur-md bg-background/40 border border-white/10',
      },
    },
    defaultVariants: {
      variant: 'pill',
    },
  }
);

interface Tab {
  id: string;
  label: string;
  icon?: LucideIcon;
  badge?: number;
  disabled?: boolean;
}

export interface CinematicTabNavProps extends VariantProps<typeof tabNavVariants> {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  className?: string;
}

export const CinematicTabNav = React.forwardRef<HTMLDivElement, CinematicTabNavProps>(
  ({ tabs, activeTab, onTabChange, variant, className }, ref) => {
    const [indicatorStyle, setIndicatorStyle] = React.useState({ left: '0', width: '0' });
    const tabsRef = React.useRef<(HTMLButtonElement | null)[]>([]);

    React.useEffect(() => {
      const activeIndex = tabs.findIndex(t => t.id === activeTab);
      const activeTabEl = tabsRef.current[activeIndex];
      const container = tabsRef.current[0]?.parentElement;
      
      if (activeTabEl && container && variant === 'pill') {
        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTabEl.getBoundingClientRect();
        
        setIndicatorStyle({
          left: `${tabRect.left - containerRect.left - 4}px`,
          width: `${tabRect.width}px`,
        });
      }
    }, [activeTab, tabs, variant]);

    return (
      <div ref={ref} className={cn(tabNavVariants({ variant }), className)}>
        {/* Animated indicator for pill variant */}
        {variant === 'pill' && (
          <div
            className="absolute h-[calc(100%-8px)] bg-primary rounded-lg shadow-lg shadow-primary/25 transition-all duration-300 ease-out top-1"
            style={indicatorStyle}
          />
        )}

        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTab;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              ref={(el) => { tabsRef.current[index] = el; }}
              onClick={() => !tab.disabled && onTabChange(tab.id)}
              disabled={tab.disabled}
              className={cn(
                'relative z-10 flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-300',
                'rounded-lg whitespace-nowrap',
                variant === 'underline' && 'rounded-none px-6 pb-3',
                
                // Active styles
                isActive && variant === 'pill' && 'text-primary-foreground',
                isActive && variant === 'underline' && 'text-primary border-b-2 border-primary -mb-[1px]',
                isActive && variant === 'glass' && 'bg-primary/20 text-primary',
                
                // Inactive styles
                !isActive && 'text-muted-foreground hover:text-foreground',
                !isActive && variant === 'glass' && 'hover:bg-white/5',
                
                // Disabled
                tab.disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {tab.label}
              
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className={cn(
                  'ml-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold rounded-full px-1.5',
                  isActive ? 'bg-primary-foreground text-primary' : 'bg-primary text-primary-foreground'
                )}>
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }
);

CinematicTabNav.displayName = 'CinematicTabNav';

export default CinematicTabNav;
